import { useEffect, useRef, useState } from 'react';
import type * as firebaseui from 'firebaseui';
import 'firebaseui/dist/firebaseui.css';
import { firebaseCompatAuth } from '@/lib/firebase/client';
import { cn } from '@/lib/utils';

interface FirebaseAuthUIProps {
  nextRoute: string;
}

export function FirebaseAuthUI({ nextRoute }: FirebaseAuthUIProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const uiRef = useRef<firebaseui.auth.AuthUI | null>(null);
  const [isUiReady, setIsUiReady] = useState(false);
  const [uiError, setUiError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    setUiError(null);

    async function bootFirebaseUI() {
      try {
        const firebaseuiModule: typeof import('firebaseui') = await import('firebaseui');

        if (!isMounted || !containerRef.current) {
          return;
        }

        if (!uiRef.current) {
          uiRef.current =
            firebaseuiModule.auth.AuthUI.getInstance() ??
            new firebaseuiModule.auth.AuthUI(firebaseCompatAuth);
        }

        const uiConfig: firebaseui.auth.Config = {
          signInFlow: 'popup',
          signInSuccessUrl: nextRoute,
          signInOptions: [
            {
              provider: 'password',
              requireDisplayName: false,
            },
            'google.com',
          ],
          tosUrl: 'https://firebase.google.com/terms',
          privacyPolicyUrl: 'https://firebase.google.com/support/privacy',
          callbacks: {
            signInSuccessWithAuthResult: () => false,
            uiShown: () => isMounted && setIsUiReady(true),
            signInFailure: async (error) => {
              console.error('FirebaseUI sign-in error', error);
              if (isMounted) {
                setUiError(error?.message ?? 'Unable to complete sign-in. Please try again.');
              }
              return Promise.resolve();
            },
          },
          credentialHelper: firebaseuiModule.auth.CredentialHelper.NONE,
        };

        uiRef.current.start(containerRef.current, uiConfig);
      } catch (error) {
        console.error('Failed to initialize FirebaseUI', error);
        if (isMounted) {
          setUiError('Failed to initialize authentication widget. Refresh and try again.');
        }
      }
    }

    bootFirebaseUI();

    return () => {
      isMounted = false;
      uiRef.current?.reset();
    };
  }, [nextRoute]);

  return (
    <div className="relative">
      {!isUiReady && (
        <div className="bg-muted/40 text-muted-foreground absolute inset-0 flex flex-col items-center justify-center gap-2 rounded-2xl border border-dashed">
          <div className="border-muted-foreground size-5 animate-spin rounded-full border-2 border-b-transparent" />
          <span className="text-xs font-medium tracking-wide uppercase">
            Preparing Firebase UI…
          </span>
        </div>
      )}
      <div
        ref={containerRef}
        className={cn(
          'firebase-auth-ui bg-background/70 rounded-2xl p-1',
          isUiReady ? 'opacity-100' : 'opacity-0'
        )}
      />
      {uiError && (
        <div className="text-destructive/90 border-destructive/40 bg-destructive/5 mt-4 rounded-xl border px-4 py-3 text-sm">
          {uiError}
        </div>
      )}
    </div>
  );
}
